import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import type { LumeScreen } from "./agent/request-context";
import NotFound from "./components/NotFound";
import RouteError from "./components/RouteError";
import { isLoggedIn } from "./lib/auth";
import { sendEntryOnce } from "./lib/entry-attribution";
import {
  cameFromWelcome,
  claimLanding,
  guardWelcome,
} from "./lib/onboarding-guard";
import { validatePracticeFilterSearch } from "./lib/practice-filters";
import { parseIdList, type PraticaSessaoSearch } from "./lib/practice-scope";

/**
 * Names the screen a route IS, for the context sent to the Lume agent
 * (agent/request-context.ts). Declared on the route rather than in a lookup
 * table keyed by path, so adding a route and forgetting to name it is visible
 * here instead of silently producing "unknown" at runtime.
 *
 * OPTIONAL on purpose: TanStack derives whether `staticData` is required from
 * `{} extends StaticDataRouteOption` (router-core route.d.ts), so a required
 * field here would force every route in the app to carry one.
 */
declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    screen?: LumeScreen;
  }
}

declare module "@tanstack/history" {
  interface HistoryState {
    /** Set by the simulado builder, whose "Começar" stands in for the paper's start screen */
    simuladoDrawn?: boolean;
  }
}

// No root e não no layout autenticado: /topico também conta como entrada.
const rootRoute = createRootRoute({
  component: () => <Outlet />,
  beforeLoad: ({ preload }) => {
    if (!preload) sendEntryOnce();
  },
});

// Pages are code-split: each lazyRouteComponent import becomes its own
// chunk, fetched on first navigation (or preloaded on link hover/touch via
// defaultPreload below)
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  component: lazyRouteComponent(() => import("./pages/Login")),
});

// Outside the authenticated layout: the quiz is sat with or without a session,
// and login only comes at the end, to keep the result.
const topicoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/topico/$termId",
  validateSearch: (
    search: Record<string, unknown>
  ): { porta?: string; q1?: number; a1?: number; artigo?: number; intencao?: "simulado" } => {
    const id = (value: unknown) => {
      const n = Number(value);
      return Number.isInteger(n) && n > 0 ? n : undefined;
    };
    return {
      porta: typeof search.porta === "string" ? search.porta : undefined,
      q1: id(search.q1),
      a1: id(search.a1),
      artigo: id(search.artigo),
      intencao: search.intencao === "simulado" ? search.intencao : undefined,
    };
  },
  component: lazyRouteComponent(() => import("./pages/TopicQuiz")),
});

const artigoResumoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/artigo-resumo/$postId",
  component: lazyRouteComponent(() => import("./pages/TopicResumo")),
});

// Pathless layout: every route added under it requires a session. beforeLoad
// re-runs on router.invalidate(), which App triggers on auth events — logging
// out while on a protected page kicks straight to /login. It renders a bare
// Outlet: screens own their own chrome (onboarding is full-bleed and dark), so
// there is no shared app shell yet.
const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authenticated",
  component: () => <Outlet />,
  beforeLoad: ({ location, preload }) => {
    if (!isLoggedIn()) {
      throw redirect({
        to: "/login",
        // location.href is basepath-stripped, so it can be fed back to navigate()
        search: { redirect: location.href },
      });
    }

    // The tab's first screen after login spends the landing test's one decision,
    // whichever screen it is — only Início acts on it (decideLanding)
    return { isLanding: !preload && claimLanding() };
  },
});

// ?ano=<taxonomy year id> carries the selected school year between the
// onboarding screen and the flows, so a flow can also be linked to directly.
// Anything unparseable is dropped and the flow asks for the year again.
function validateYearSearch(search: Record<string, unknown>): { ano?: number } {
  const ano = Number(search.ano);
  return { ano: Number.isInteger(ano) && ano > 0 ? ano : undefined };
}

// `materias` is a comma list of subject ids to start ticked, e.g. "1,2,9". A single
// id arrives as a number: the router JSON-parses search values.
function validateNovoSearch(
  search: Record<string, unknown>
): { ano?: number; materias?: string } {
  const materias = search.materias === undefined ? "" : String(search.materias);
  return {
    ...validateYearSearch(search),
    materias: /^\d+(,\d+)*$/.test(materias) ? materias : undefined,
  };
}

// Everything OUTSIDE a caderno sits under one shell: Início, the cadernos list
// and the corrector running as a tool of its own. A pathless layout route, so it
// owns the sidebar and the bottom bar while its children own the content.
//
// The build flows (/novo/*) are deliberately NOT under it — they are full-bleed
// screens that end in a created caderno, and a nav column beside them would
// invite the student to wander off halfway through.
const hubRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  id: "hub",
  component: lazyRouteComponent(() => import("./pages/HubLayout")),
  // An unknown path at the hub keeps the shell, the same way an unknown path
  // under a caderno does: the sidebar is still the way out, so the NotFound sits
  // in the content column rather than taking over the screen.
  notFoundComponent: () => <NotFound embedded />,
});

// The hub's home: the greeting, the way to start a caderno, the cadernos the
// student already has, and the tools that belong to no caderno.
//
// It replaced the ENEM chooser that used to be `/`, which in turn had replaced
// the conversational hub (pages/Onboarding.tsx) — kept in the codebase but no
// longer routed. The chooser is gone outright: with one build flow there is
// nothing left to choose between.
const inicioRoute = createRoute({
  getParentRoute: () => hubRoute,
  path: "/",
  validateSearch: validateYearSearch,
  staticData: { screen: "hub" },
  // The ONE piece of onboarding left outside a caderno: a student who lands on
  // exactly /lume/ with no caderno yet is welcomed and sent to build one. See
  // guardWelcome — it fires here and on no other hub screen. The landing test
  // (Início or the Tutor) is decided in the same guard.
  beforeLoad: ({ context, location, preload }) =>
    guardWelcome({
      fromWelcome: cameFromWelcome(location.state),
      isLanding: context.isLanding,
      preload,
    }),
  component: lazyRouteComponent(() => import("./pages/Inicio")),
});

// The way back into a caderno the student already has — the one screen that
// lists cadernos rather than building one.
const cadernosRoute = createRoute({
  getParentRoute: () => hubRoute,
  path: "/cadernos",
  // ?page= is the paging cursor. Anything unparseable falls back to page 1
  // rather than erroring — a mangled link should still show the list. The API
  // clamps the upper end, since only it knows how many pages there are.
  validateSearch: (search: Record<string, unknown>): { page: number } => {
    const page = Number(search.page);
    return { page: Number.isInteger(page) && page > 0 ? page : 1 };
  },
  staticData: { screen: "cadernos" },
  component: lazyRouteComponent(() => import("./pages/Cadernos")),
});

/*
 * ── the corrector, twice ──────────────────────────────────────────────────
 *
 * The essay corrector runs at BOTH levels: as a tool on the hub (`/redacao`),
 * and inside a caderno (`/caderno/7/redacao`). Same three screens — writing, the
 * list of previous essays, one correction — and the same components, registered
 * under two parents.
 *
 * The difference is what each list contains. Inside a caderno the list is only
 * the essays written there; on the hub it is all of them, each tagged with the
 * caderno it came from. The screens read which they are from the router itself
 * (useParams({ strict: false }) — no cadernoId means the hub), so nothing has to
 * be threaded through and there is only one copy of each page.
 *
 * The three `screen` names are shared too. The agent tells the two apart by
 * whether the context carries a cadernoId, which is the real signal — a screen
 * name that differed only in its prefix would be a second way to say the same
 * thing, and a way for the two to disagree.
 */

// ?tema=<essays_themes id> is which proposta is open. In the URL rather than in
// the page's state so a tema is linkable (the chatbot can drop a student
// straight onto one) and the back button steps out of the writer instead of
// leaving the screen. Absent means the chooser is up.
function validateTemaSearch(search: Record<string, unknown>): { tema?: number } {
  const tema = Number(search.tema);
  return Number.isInteger(tema) && tema > 0 ? { tema } : {};
}

// Same pager contract as the cadernos list: anything unparseable falls back to
// page 1 rather than erroring, and the API clamps the upper end.
function validatePageSearch(search: Record<string, unknown>): { page: number } {
  const page = Number(search.page);
  return { page: Number.isInteger(page) && page > 0 ? page : 1 };
}

/*
 * ── the Tutor, twice ──────────────────────────────────────────────────────
 *
 * The chat as a SCREEN — /lume/chat and /lume/caderno/:id/chat — rather than as the
 * drawer that slides over whatever page the student is on. Same component
 * (pages/Tutor.tsx) under two parents, exactly like the corrector above, and for the
 * same reason: it is one screen whose only difference is whose conversation it shows.
 *
 * Which Lume answers follows from the level and not from the route: the caderno's is
 * the study tutor that can read the caderno, the hub's is the one that knows the
 * student's cadernos and the catalogue (agent/chat-session.ts). They keep SEPARATE
 * screen names, unlike the corrector's shared three, because here the name is the
 * only thing distinguishing them — there is no cadernoId in the payload to tell the
 * two apart, since the hub's turns carry none.
 */
// ?de=balao: the student arrives from the balão's quiz, with the result in
// the tab (api/topic-quiz.ts storedTopicQuizOutcome).
const tutorRoute = createRoute({
  getParentRoute: () => hubRoute,
  path: "/chat",
  validateSearch: (search: Record<string, unknown>): { de?: "balao" } =>
    search.de === "balao" ? { de: "balao" } : {},
  staticData: { screen: "hub_tutor" },
  component: lazyRouteComponent(() => import("./pages/Tutor")),
});

const redacaoRoute = createRoute({
  getParentRoute: () => hubRoute,
  path: "/redacao",
  validateSearch: validateTemaSearch,
  staticData: { screen: "redacao" },
  component: lazyRouteComponent(() => import("./pages/redacao/Redacao")),
});

const redacaoMinhasRoute = createRoute({
  getParentRoute: () => hubRoute,
  path: "/redacao/minhas",
  validateSearch: validatePageSearch,
  staticData: { screen: "redacao_minhas" },
  component: lazyRouteComponent(() => import("./pages/redacao/RedacaoMinhas")),
});

const redacaoCorrecaoRoute = createRoute({
  getParentRoute: () => hubRoute,
  path: "/redacao/correcao/$correcaoId",
  staticData: { screen: "redacao_correcao" },
  component: lazyRouteComponent(() => import("./pages/redacao/RedacaoCorrecao")),
});

/*
 * ── the Simulados screen, twice ───────────────────────────────────────────
 *
 * The previous-year ENEM provas, at both levels like the corrector: on the hub
 * (/simulados) and inside a caderno (/caderno/7/simulados). Same components; the
 * screens read the cadernoId from the router. Outside a caderno a prova is saved
 * and scored but moves no mastery.
 *
 * ?tentativa=<attempt id> on the prova reopens a finished sitting for its
 * results instead of starting a new one.
 */
function validateProvaSearch(search: Record<string, unknown>): {
  tentativa?: number;
} {
  const tentativa = Number(search.tentativa);
  return Number.isInteger(tentativa) && tentativa > 0 ? { tentativa } : {};
}

const simuladosRoute = createRoute({
  getParentRoute: () => hubRoute,
  path: "/simulados",
  staticData: { screen: "simulados" },
  component: lazyRouteComponent(() => import("./pages/simulados/Simulados")),
});

const provaRoute = createRoute({
  getParentRoute: () => hubRoute,
  path: "/simulados/$simuladoId",
  validateSearch: validateProvaSearch,
  staticData: { screen: "prova" },
  component: lazyRouteComponent(() => import("./pages/simulados/Prova")),
});

const praticarRoute = createRoute({
  getParentRoute: () => hubRoute,
  path: "/praticar",
  // The chosen matéria rides the URL so the browser's back leaves its tópicos
  // for the matéria grid.
  validateSearch: (search: Record<string, unknown>): { materia?: number } => {
    const materia = Number(search.materia);
    return Number.isInteger(materia) && materia > 0 ? { materia } : {};
  },
  staticData: { screen: "pratica_hub" },
  component: lazyRouteComponent(() => import("./pages/PraticarPorMateria")),
});

function validateBuilderSearch(search: Record<string, unknown>): {
  tamanho?: 10 | 20;
} {
  const tamanho = Number(search.tamanho);
  return tamanho === 10 || tamanho === 20 ? { tamanho } : {};
}

// The simulado builder and the paper it draws, outside any caderno: drawn from
// the ENEM ano, saved without moving mastery. Same screens as the caderno's
// /praticar/simulado pair; "montar" and "livre" are static segments, so they win
// over $simuladoId.
const simuladoNovoRoute = createRoute({
  getParentRoute: () => hubRoute,
  path: "/simulados/montar",
  validateSearch: validateBuilderSearch,
  staticData: { screen: "simulado_novo" },
  component: lazyRouteComponent(() => import("./pages/caderno/SimuladoNovo")),
});

const simuladoLivreRoute = createRoute({
  getParentRoute: () => hubRoute,
  path: "/simulados/livre/$attemptId",
  validateSearch: (search: Record<string, unknown>): { tempo?: true } =>
    search.tempo === true || search.tempo === "true" ? { tempo: true } : {},
  remountDeps: ({ params }) => params,
  staticData: { screen: "simulado_livre" },
  component: lazyRouteComponent(() => import("./pages/caderno/SimuladoLivre")),
});

// The hello — /lume/bem-vindo. All that is left of Lume's onboarding as a PLACE:
// a student with no caderno gets one screen that greets them and sends them to
// build one. The seven mocked walkthroughs that used to be routed are gone, and
// the caderno's — the only one that survived — is now six tooltips mounted over
// the real Painel (components/onboarding/CadernoTour), which needs no URL.
//
// Outside hubRoute, and that is the point: it is full-bleed, with no sidebar. A
// student on it has nothing to navigate to yet.
//
// No `screen`: no chat is mounted on it, and naming it would put a screen in the
// agent's vocabulary that no tool can act on.
const welcomeRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/bem-vindo",
  component: lazyRouteComponent(() => import("./pages/Welcome")),
});

// THE build flow. One route, because there is one flow: the completo/materias/
// prova split is gone, and with it the screen that made the student choose
// between them before they knew what the difference was (see pages/CadernoNovo).
//
// It carries a `screen` even though no chat is mounted on it yet: the name is
// what a route IS, and naming it here is what keeps `unknown` impossible if a
// chat ever opens over the flow.
const novoRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/novo",
  validateSearch: validateNovoSearch,
  staticData: { screen: "novo" },
  component: lazyRouteComponent(() => import("./pages/CadernoNovo")),
});

/**
 * The old per-type paths, kept as redirects rather than deleted.
 *
 * Not for old bookmarks — for the AGENT. `show_link` names a route id from a
 * list that lives in the Mastra service (a separate repo), so it can still emit
 * novo_completo or novo_materias until that ships, and LinkWidget navigates to
 * whatever path it is handed. A 404 in the middle of a chat is a worse answer
 * than the flow those ids were asking for, which is now this one.
 *
 * `?ano=` rides along: it is the only thing those paths carried.
 */
const LEGACY_NOVO_PATHS = ["/novo/completo", "/novo/materias", "/novo/prova"] as const;

const legacyNovoRoutes = LEGACY_NOVO_PATHS.map((path) =>
  createRoute({
    getParentRoute: () => authenticatedRoute,
    path,
    validateSearch: validateYearSearch,
    staticData: { screen: "novo" },
    beforeLoad: ({ search }) => {
      throw redirect({ to: "/novo", search, replace: true });
    },
  })
);

// Where the three flows land once the caderno exists. A layout route: it owns
// the sidebar and the ownership check, its children own the content.
const cadernoRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/caderno/$cadernoId",
  component: lazyRouteComponent(() => import("./pages/caderno/CadernoLayout")),
  // An unknown path UNDER a caderno (a mistyped /materias) keeps the shell: the
  // caderno is real, only the sub-page is not. Rendered in CadernoLayout's Outlet,
  // so the sidebar stays and the NotFound sits in the content column (embedded) —
  // rather than the root default, which owns the whole screen. Top-level unknown
  // paths still fall to the router's defaultNotFoundComponent.
  notFoundComponent: () => <NotFound embedded />,
});

// The caderno's home is the Painel — a dashboard of cards (continuar, trilha,
// matérias, praticar). It keeps the "caderno_inicio" screen name so everything
// that names the caderno home (agent tools, the link widget) still resolves
// here; the trilha it used to be is now one card away at /trilha.
const cadernoInicioRoute = createRoute({
  getParentRoute: () => cadernoRoute,
  path: "/",
  staticData: { screen: "caderno_inicio" },
  component: lazyRouteComponent(() => import("./pages/caderno/Painel")),
});

// The year's roteiro drawn as a path. No longer the caderno's home — that is now
// the Painel at the bare URL — so it carries its own "trilha" screen, letting the
// agent (and its links) tell "the caderno home" apart from "the trilha screen".
const cadernoTrilhaRoute = createRoute({
  getParentRoute: () => cadernoRoute,
  path: "/trilha",
  staticData: { screen: "trilha" },
  // `?modo=completo|essencial` lands with that trilha selected and persists it —
  // the agent's trilha_completo / trilha_essencial links. Anything else is dropped.
  validateSearch: (
    search: Record<string, unknown>
  ): { modo?: "completo" | "essencial" } =>
    search.modo === "completo" || search.modo === "essencial"
      ? { modo: search.modo }
      : {},
  component: lazyRouteComponent(() => import("./pages/caderno/Trilha")),
});

// The matéria grid: every matéria in the caderno as a card, with a status panel
// that opens below when one is picked. Replaced the sidebar's per-matéria list.
const cadernoMateriasRoute = createRoute({
  getParentRoute: () => cadernoRoute,
  path: "/materias",
  // The open matéria rides in the query string (?materia=<taxonomy subject id>),
  // not the path: the grid stays one screen, but the selection is deep-linkable
  // and — the reason it is a URL and not React state — visible to the Lume agent,
  // which reads the route to know which matéria the student is looking at.
  validateSearch: (search: Record<string, unknown>): { materia?: number } => {
    const materia = Number(search.materia);
    return Number.isInteger(materia) && materia > 0 ? { materia } : {};
  },
  staticData: { screen: "materia" },
  component: lazyRouteComponent(() => import("./pages/caderno/Materias")),
});

// One page per matéria in the caderno. Keyed by the taxonomy subject id, which
// is unique within a caderno and readable in the URL.
const cadernoMateriaRoute = createRoute({
  getParentRoute: () => cadernoRoute,
  path: "/materia/$subjectId",
  staticData: { screen: "materia" },
  component: lazyRouteComponent(() => import("./pages/caderno/Materia")),
});

// The three study screens. Flat under the caderno rather than nested under the
// matéria, because Materia is a leaf that renders content, not a layout with an
// Outlet — they replace it rather than appear inside it.
//
// The matéria stays in the path even though the term id alone would resolve:
// it is what the sidebar highlights, and it lets these screens read the
// already-cached matéria tree instead of asking for a term on its own.
const cadernoTopicoRoute = createRoute({
  getParentRoute: () => cadernoRoute,
  path: "/materia/$subjectId/topico/$termId",
  staticData: { screen: "topico" },
  component: lazyRouteComponent(() => import("./pages/caderno/Topico")),
});

const cadernoPraticarRoute = createRoute({
  getParentRoute: () => cadernoRoute,
  path: "/materia/$subjectId/topico/$termId/praticar",
  staticData: { screen: "praticar" },
  component: lazyRouteComponent(() => import("./pages/caderno/Praticar")),
});

// The "Estudar melhor" hub: find a matéria, tema or tópico and open Prática
// infinita on it. Flat under the caderno like the study screens — it belongs to
// no matéria, so there is no subject in its path. Not fullscreen: the page keeps
// the sidebar, and the infinite session is its own fullscreen route.
//
// The filters live in the URL so the session's "back" lands on them again.
const cadernoPraticaRoute = createRoute({
  getParentRoute: () => cadernoRoute,
  path: "/praticar",
  validateSearch: validatePracticeFilterSearch,
  staticData: { screen: "pratica_hub" },
  component: lazyRouteComponent(() => import("./pages/caderno/Pratica")),
});

// The Prática infinita session, on its own URL so the chatbot (and any link or
// notification) can drop the student straight into it. Fullscreen — the shell
// drops the sidebar on the "pratica_sessao" screen.
//
// The scope is the narrowest of topicos > tema > materia (absent = whole
// caderno). modo is "Só o que já estudei": 'guiado' is the default, so only
// 'completo' is carried. rotulo names a set of tópicos in the header ("18
// tópicos · movimento") — the search term that found them, which only the caller
// knows.
const cadernoPraticaSessaoRoute = createRoute({
  getParentRoute: () => cadernoRoute,
  path: "/praticar/sessao",
  validateSearch: (search: Record<string, unknown>): PraticaSessaoSearch => {
    const materia = Number(search.materia);
    const tema = Number(search.tema);
    const topicos = parseIdList(search.topicos);
    const rotulo =
      typeof search.rotulo === "string" && search.rotulo.trim() !== ""
        ? search.rotulo.trim().slice(0, 60)
        : undefined;
    const scoped: PraticaSessaoSearch = {};

    if (Number.isInteger(materia) && materia > 0) {
      scoped.materia = materia;
    }
    if (Number.isInteger(tema) && tema > 0) {
      scoped.tema = tema;
    }
    if (topicos.length > 0) {
      scoped.topicos = topicos;
    }
    if (rotulo !== undefined) {
      scoped.rotulo = rotulo;
    }

    return search.modo === "completo" ? { ...scoped, modo: "completo" } : scoped;
  },
  staticData: { screen: "pratica_sessao" },
  component: lazyRouteComponent(() => import("./pages/caderno/PraticaSessao")),
});

// The simulado builder: "Montar um simulado". Under /praticar because that is
// where it is entered from and what it belongs to — it keeps the sidebar, like
// the Praticar dashboard it hangs off.
const cadernoSimuladoNovoRoute = createRoute({
  getParentRoute: () => cadernoRoute,
  path: "/praticar/simulado",
  validateSearch: validateBuilderSearch,
  staticData: { screen: "simulado_novo" },
  component: lazyRouteComponent(
    () => import("./pages/caderno/SimuladoNovo")
  ),
});

// The on-demand simulado itself. Keyed on the ATTEMPT and not on the scope that
// produced it: the selection can mix matérias, temas and tópicos, which does not
// fit a URL, and re-running it on every refresh would draw a different paper each
// time. Fullscreen — the shell drops the sidebar on the "simulado_livre" screen.
const cadernoSimuladoLivreRoute = createRoute({
  getParentRoute: () => cadernoRoute,
  path: "/praticar/simulado/$attemptId",
  // ?tempo=1 says the student asked for the clock. It rides in the URL because
  // the timer is purely client-side (lib/simulado.ts) — nothing server-side
  // records that a paper was timed, and nothing needs to.
  validateSearch: (search: Record<string, unknown>): { tempo?: true } =>
    search.tempo === true || search.tempo === "true" ? { tempo: true } : {},
  // A chat link from one paper to another keeps the route: without a remount the
  // runner, which opens its sitting once, would go on showing the first paper.
  remountDeps: ({ params }) => params,
  staticData: { screen: "simulado_livre" },
  component: lazyRouteComponent(
    () => import("./pages/caderno/SimuladoLivre")
  ),
});

// $termId is the TEMA here, not a tópico — named the same as the route above so
// one QuizRunner serves both without a second param vocabulary.
const cadernoExameRoute = createRoute({
  getParentRoute: () => cadernoRoute,
  path: "/materia/$subjectId/tema/$termId/exame",
  staticData: { screen: "exame" },
  component: lazyRouteComponent(() => import("./pages/caderno/Exame")),
});

// A material de apoio from the trilha: cross-matéria like a simulado, so keyed
// on its own id with no subject in the path.
const cadernoMaterialRoute = createRoute({
  getParentRoute: () => cadernoRoute,
  path: "/material/$manualPageId",
  staticData: { screen: "material" },
  component: lazyRouteComponent(() => import("./pages/caderno/Material")),
});

// The simulado — a cross-matéria mock exam. Flat under the caderno like the
// study screens, but keyed on $simuladoId rather than a subject/term: it belongs
// to no matéria, so there is no subject in its path.
const cadernoSimuladoRoute = createRoute({
  getParentRoute: () => cadernoRoute,
  path: "/simulado/$simuladoId",
  validateSearch: validateProvaSearch,
  // Remounts on another simulado, as the on-demand one above, but not on
  // ?tentativa: "Refazer" leaves the review for the start screen in place.
  remountDeps: ({ params }) => params,
  staticData: { screen: "simulado" },
  component: lazyRouteComponent(() => import("./pages/caderno/Simulado")),
});

// The Simulados screen and a prova inside a caderno — see the hub's copies above.
const cadernoSimuladosRoute = createRoute({
  getParentRoute: () => cadernoRoute,
  path: "/simulados",
  staticData: { screen: "simulados" },
  component: lazyRouteComponent(() => import("./pages/simulados/Simulados")),
});

const cadernoProvaRoute = createRoute({
  getParentRoute: () => cadernoRoute,
  path: "/simulados/$simuladoId",
  validateSearch: validateProvaSearch,
  staticData: { screen: "prova" },
  component: lazyRouteComponent(() => import("./pages/simulados/Prova")),
});

// The Tutor inside a caderno: the same screen as the hub's, under the caderno's
// shell and talking to the caderno's own tutor. See the block above the hub's copy.
//
// Flat under the caderno like the study screens, and NOT fullscreen: the whole
// point of the page over the drawer's "tela cheia" is that the nav stays — the shell
// gives it a content column one viewport tall and the chat fills it (CadernoLayout).
const cadernoTutorRoute = createRoute({
  getParentRoute: () => cadernoRoute,
  path: "/chat",
  staticData: { screen: "tutor" },
  component: lazyRouteComponent(() => import("./pages/Tutor")),
});

// The corrector inside a caderno — the same three screens as the hub's, under
// the caderno's shell and scoped to the essays written here. See the block above
// the hub's copies for why one set of components serves both.
const cadernoRedacaoRoute = createRoute({
  getParentRoute: () => cadernoRoute,
  path: "/redacao",
  validateSearch: validateTemaSearch,
  staticData: { screen: "redacao" },
  component: lazyRouteComponent(() => import("./pages/redacao/Redacao")),
});

const cadernoRedacaoMinhasRoute = createRoute({
  getParentRoute: () => cadernoRoute,
  path: "/redacao/minhas",
  validateSearch: validatePageSearch,
  staticData: { screen: "redacao_minhas" },
  component: lazyRouteComponent(() => import("./pages/redacao/RedacaoMinhas")),
});

// One correction, keyed by essays_corrections.id. Its own URL so a result is
// linkable and so handing an essay in and opening it from the list a month later
// land on exactly the same screen.
const cadernoRedacaoCorrecaoRoute = createRoute({
  getParentRoute: () => cadernoRoute,
  path: "/redacao/correcao/$correcaoId",
  staticData: { screen: "redacao_correcao" },
  component: lazyRouteComponent(() => import("./pages/redacao/RedacaoCorrecao")),
});

// Every Lume page except /login, /topico and /artigo-resumo sits under the authenticated layout
const routeTree = rootRoute.addChildren([
  loginRoute,
  topicoRoute,
  artigoResumoRoute,
  authenticatedRoute.addChildren([
    hubRoute.addChildren([
      inicioRoute,
      cadernosRoute,
      simuladosRoute,
      praticarRoute,
      simuladoNovoRoute,
      simuladoLivreRoute,
      provaRoute,
      tutorRoute,
      redacaoRoute,
      redacaoMinhasRoute,
      redacaoCorrecaoRoute,
    ]),
    welcomeRoute,
    novoRoute,
    ...legacyNovoRoutes,
    cadernoRoute.addChildren([
      cadernoInicioRoute,
      cadernoTrilhaRoute,
      cadernoMateriasRoute,
      cadernoMateriaRoute,
      cadernoTopicoRoute,
      cadernoPraticarRoute,
      cadernoPraticaRoute,
      cadernoPraticaSessaoRoute,
      cadernoSimuladoNovoRoute,
      cadernoSimuladoLivreRoute,
      cadernoExameRoute,
      cadernoSimuladoRoute,
      cadernoMaterialRoute,
      cadernoSimuladosRoute,
      cadernoProvaRoute,
      cadernoTutorRoute,
      cadernoRedacaoRoute,
      cadernoRedacaoMinhasRoute,
      cadernoRedacaoCorrecaoRoute,
    ]),
  ]),
]);

// The PHP router sends every /lume/* URL to the same shell (lume.php),
// so the router owns everything under /lume
export const router = createRouter({
  routeTree,
  basepath: "/lume",
  defaultPreload: "intent",
  // PHP funnels every /lume/* URL to the same shell, so an unknown path reaches
  // the router rather than the site's 404 — it has to answer for itself
  defaultNotFoundComponent: () => <NotFound />,
  // A screen that throws shows the friendly CircleBuddy error, never TanStack's
  // built-in box with the raw message and stack — which a student must not see.
  defaultErrorComponent: RouteError,
});

// Registers the router instance so Link/useNavigate are typed against it
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
